import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IonIcon } from '@ionic/react';
import { cubeOutline } from 'ionicons/icons';
import BrowserWindow from 'components/atoms/BrowserWindow';
import './TestRuns.css';

const STEPS = [
  {
    command: 'npm install -g @zibby/cli',
    output: [
      { text: 'Resolving dependencies...', cls: '' },
      { text: 'Downloading @zibby/cli@0.1.0...', cls: '' },
      { text: 'added 142 packages in 8s', cls: '' },
      { text: '\u2713 @zibby/cli@0.1.0 installed', cls: 'gss-success' },
    ],
  },
  {
    command: 'cd your-project && zibby init --agent cursor --cloud-sync --headless',
    output: [
      { text: '\u2699 Setting up with provided options...', cls: '' },
      { text: '  Detecting project type... React (TypeScript)', cls: '' },
      { text: '  Configuring cloud sync endpoint...', cls: '' },
      { text: '\u2713 Zibby initialized!', cls: 'gss-success' },
      { text: '\u2713 Playwright MCP configured', cls: 'gss-success' },
      { text: '\u2713 .zibby/config.yml created', cls: 'gss-success' },
    ],
  },
  {
    command: 'zibby test test-specs/auth/login.txt --project zby_rZWruDn6rb1d9ZnRs5HHi3l6 --collection "My Tests"',
    output: [
      { text: '\u25B6 Starting browser...', cls: '' },
      { text: '  \u2192 Navigate to /login', cls: '' },
      { text: '  \u2192 Fill email: test@acme.com', cls: '' },
      { text: '  \u2192 Fill password: ••••••••', cls: '' },
      { text: '  \u2192 Click "Sign in"', cls: '' },
      { text: '  \u2192 Assert redirect to /dashboard', cls: '' },
      { text: '\u2713 Test execution complete (4.2s)', cls: 'gss-success' },
      { text: '\uD83D\uDCE6 Uploading to Zibby Cloud...', cls: '' },
      { text: '\uD83C\uDFA5 Video: https://zibby.app/executions/...', cls: 'gss-link-line' },
    ],
  },
];

const CHAR_SPEED = 18;
const OUTPUT_LINE_DELAY = 120;
const STEP_PAUSE = 300;

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const TestRuns = () => {
  const [visibleSteps, setVisibleSteps] = useState([]);
  const cancelRef = useRef(false);
  const timeoutsRef = useRef([]);

  const clearAllTimeouts = useCallback(() => {
    cancelRef.current = true;
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const delay = useCallback((ms) => {
    return new Promise((resolve) => {
      const t = setTimeout(() => {
        if (!cancelRef.current) resolve();
      }, ms);
      timeoutsRef.current.push(t);
    });
  }, []);

  useEffect(() => {
    cancelRef.current = false;

    const animate = async () => {
      // Wait for the wrapper's CSS fade-in (320ms) to finish before typing
      // — otherwise the first command appears mid-fade and the toggle feels
      // like nothing changed.
      await delay(380);
      for (let si = 0; si < STEPS.length; si++) {
        if (cancelRef.current) return;
        const step = STEPS[si];

        // Type command character by character
        for (let ci = 1; ci <= step.command.length; ci++) {
          if (cancelRef.current) return;
          setVisibleSteps(prev => {
            const next = [...prev];
            next[si] = { commandText: step.command.slice(0, ci), outputLines: [], done: false };
            return next;
          });
          await delay(CHAR_SPEED);
        }

        // Show output lines one by one
        for (let oi = 0; oi < step.output.length; oi++) {
          if (cancelRef.current) return;
          await delay(OUTPUT_LINE_DELAY);
          setVisibleSteps(prev => {
            const next = [...prev];
            const current = { ...next[si] };
            current.outputLines = [...current.outputLines, step.output[oi]];
            next[si] = current;
            return next;
          });
        }

        // Mark step done
        setVisibleSteps(prev => {
          const next = [...prev];
          next[si] = { ...next[si], done: true };
          return next;
        });

        if (si < STEPS.length - 1) {
          await delay(STEP_PAUSE);
        }
      }
    };

    animate();
    return clearAllTimeouts;
  }, [delay, clearAllTimeouts]);

  return (
    <div className="gss-wrapper">
      <div className="gss-header">
        <IonIcon icon={cubeOutline} className="gss-header-icon" />
        <span className="gss-header-text">Run a plain-English browser test in 3 steps</span>
      </div>
      <BrowserWindow variant="dark" className="gss-terminal">
        <div className="gss-terminal-body">
          {STEPS.map((step, si) => {
            const vis = visibleSteps[si];
            if (!vis) return null;
            return (
              <div key={si} className="gss-step">
                <div className="gss-command">
                  <span className="gss-prompt">$</span>
                  <span className="gss-command-text">
                    {vis.commandText}
                    {!vis.done && vis.outputLines.length === 0 && <span className="gss-cursor">|</span>}
                  </span>
                  {vis.done && (
                    <button className="gss-copy-btn" onClick={() => navigator.clipboard?.writeText(step.command)} aria-label="Copy command">
                      <CopyIcon />
                    </button>
                  )}
                </div>
                {vis.outputLines.length > 0 && (
                  <div className="gss-output">
                    {vis.outputLines.map((line, li) => (
                      <span key={li} className={`gss-output-line gss-indent ${line.cls}`}>
                        {line.cls === 'gss-link-line'
                          ? <>{line.text.split('https://')[0]}<a href="https://zibby.app/executions/" className="gss-link" target="_blank" rel="noopener noreferrer">https://zibby.app/executions/...</a></>
                          : line.text
                        }
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </BrowserWindow>
    </div>
  );
};

export default TestRuns;
